import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import axios from 'src/utils/axios';
import Iconify from 'src/components/Iconify';
import v2_actionDetalhesCanal from 'src/actions/v2/cliente/canais/v2_actionDetalhesCanal';
import v2_actionEditarCanal from 'src/actions/v2/cliente/canais/v2_actionEditarCanal';
import Config from 'src/config';
import { Card, CardContent } from 'src/@/components/ui/card';

// ----------------------------------------------------------------------

const FB_CHANNEL_NAME = 'fb_oauth_channel';
const FB_STORAGE_KEY = 'fb_oauth_code';

const DEBUG_SCENARIOS = [
    { value: 'success', label: 'Sucesso (com WABAs + phones)' },
    { value: 'success_no_waba', label: 'Sucesso (sem WABAs)' },
    { value: 'error_token', label: 'Erro na troca de token' },
    { value: 'error_graph', label: 'Erro na Graph API' },
];

export default function FacebookConnectPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const hasCalledLogin = useRef(false);
    const popupRef = useRef(null);
    const statusRef = useRef('idle');
    const pollingRef = useRef(null);

    // Params expected from URL
    const appId = params.get('app_id');
    const configId = params.get('config_id');
    const channelId = params.get('channel_id');
    const code = params.get('code');

    const [status, setStatus] = useState('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [errorDebugInfo, setErrorDebugInfo] = useState(null);
    const [showDebugDetails, setShowDebugDetails] = useState(false);
    const [progressMessage, setProgressMessage] = useState('Inicializando...');
    const [wabas, setWabas] = useState([]);
    const [selectedWaba, setSelectedWaba] = useState(null);
    const [selectedPhone, setSelectedPhone] = useState(null);
    const [tokenData, setTokenData] = useState(null);
    const [isPopup, setIsPopup] = useState(false);

    // Mode selection state
    const [signupMode, setSignupMode] = useState(null); // 'cloud_api' | 'coexistence'

    // Debug mode state (dev only)
    const [debugMode, setDebugMode] = useState(false);
    const [debugScenario, setDebugScenario] = useState('success');

    const updateStatus = useCallback((newStatus) => {
        statusRef.current = newStatus;
        setStatus(newStatus);
    }, []);

    // --- Popup detection: if we have ?code=, we are the popup returning from Facebook ---
    const isPopupWindow = Boolean(code);

    // Send code to parent via all available channels and close
    useEffect(() => {
        if (!isPopupWindow) return;

        setIsPopup(true);

        // 1. BroadcastChannel (most reliable same-origin communication)
        try {
            const bc = new BroadcastChannel(FB_CHANNEL_NAME);
            bc.postMessage({ type: 'FB_CODE', code });
            bc.close();
        } catch (_) { /* BroadcastChannel not supported */ }

        // 2. postMessage to opener (works if opener ref survived)
        try {
            if (window.opener) {
                window.opener.postMessage({ type: 'FB_CODE', code }, window.location.origin);
            }
        } catch (_) { /* opener blocked */ }

        // 3. localStorage fallback (triggers 'storage' event in parent)
        localStorage.setItem(FB_STORAGE_KEY, code);

        // 4. Try to close popup
        try {
            window.close();
        } catch (_) { /* close blocked */ }
    }, [isPopupWindow, code]);

    // --- Parent window logic ---

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    const processExchangeResponse = useCallback((resData) => {
        const { accessToken, expiresIn, wabas: wabasData, wabaFetchError } = resData;
        if (!accessToken) {
            updateStatus('error');
            setErrorMessage('Token não recebido do Facebook.');
            return;
        }

        setTokenData({ accessToken, expiresIn });

        if (wabasData && wabasData.length > 0 && channelId) {
            setWabas(wabasData);
            if (wabasData.length === 1) {
                setSelectedWaba(wabasData[0]);
                if (wabasData[0].phoneNumbers?.length === 1) {
                    setSelectedPhone(wabasData[0].phoneNumbers[0]);
                }
            }
            updateStatus('selecting_waba');
        } else if (channelId) {
            saveTokenToChannel(accessToken, null, null, null, expiresIn);
        } else {
            updateStatus('success');
        }

        if (wabaFetchError) {
            console.warn('Não foi possível buscar WABAs automaticamente.');
        }
    }, [channelId, updateStatus]);

    const handleExchangeCode = useCallback((receivedCode) => {
        if (statusRef.current === 'exchanging_token' || statusRef.current === 'success') return;
        stopPolling();
        updateStatus('exchanging_token');
        setProgressMessage('Trocando código de autorização por token...');

        const baseUrl = Config.dev ? Config.url_dev : Config.urlBackend;
        const requestUrl = `${baseUrl}/api/v1/canais/exchange-facebook-code`;

        if (Config.dev) {
            console.group('[FB SDK] Exchanging code for token');
            console.log('App ID (env):', import.meta.env.VITE_FACEBOOK_APP_ID);
            console.log('Code (first 20 chars):', receivedCode?.substring(0, 20) + '...');
            console.groupEnd();
        }

        axios
            .post(requestUrl, { code: receivedCode })
            .then((res) => {
                const data = res.data?.data || res.data;
                processExchangeResponse(data);
            })
            .catch((error) => {
                updateStatus('error');

                const fbMessage = error.response?.data?.message || error.message || error.data?.message;
                const fbDetails = error.response?.data?.details || error.details || error.data?.details;
                const debugInfo = error.response?.data?.debug || error.debug || error.data?.debug;

                let msg = 'Falha na troca de token';
                if (fbMessage) msg += ': ' + fbMessage;
                if (fbDetails?.error?.message) msg += ' (' + fbDetails.error.message + ')';

                setErrorMessage(msg);

                if (Config.dev) {
                    const info = { fbMessage, fbDetails, debugInfo, fullError: error.response?.data || error.data || error };
                    setErrorDebugInfo(info);
                    console.group('[FB OAuth] Token exchange FAILED');
                    console.error('Message:', fbMessage);
                    console.error('FB Details:', fbDetails);
                    console.error('Debug info:', debugInfo);
                    console.error('Full error:', error);
                    console.groupEnd();
                }
            });
    }, [channelId, updateStatus, stopPolling, processExchangeResponse]);

    const handleDebugExchange = useCallback(async () => {
        updateStatus('exchanging_token');
        setProgressMessage('Simulando troca de token (debug)...');
        setErrorDebugInfo(null);

        const baseUrl = Config.dev ? Config.url_dev : Config.urlBackend;
        const requestUrl = `${baseUrl}/api/v1/canais/debug-facebook-exchange`;

        try {
            const res = await axios.post(requestUrl, { scenario: debugScenario });
            const data = res.data?.data || res.data;

            if (res.data?.error) {
                updateStatus('error');
                setErrorMessage(res.data.message || 'Debug: erro simulado');
                if (Config.dev) {
                    setErrorDebugInfo({ debugScenario, fullError: res.data });
                }
                return;
            }

            processExchangeResponse(data);
        } catch (error) {
            updateStatus('error');
            const msg = error.response?.data?.message || error.message || 'Erro no debug endpoint';
            setErrorMessage('Debug: ' + msg);
            if (Config.dev) {
                setErrorDebugInfo({ debugScenario, fullError: error.response?.data || error });
            }
        }
    }, [debugScenario, updateStatus, processExchangeResponse]);

    // Carrega o FB JS SDK uma vez e inicializa com o App ID configurado.
    // O SDK abre o modal de embedded signup sem redirect — não precisa de URI.
    useEffect(() => {
        if (isPopupWindow) return;
        const currentAppId = appId || import.meta.env.VITE_FACEBOOK_APP_ID || '1553356899550775';
        const init = () => {
            window.FB.init({ appId: currentAppId, version: 'v22.0', xfbml: false, cookie: false });
        };
        if (window.FB) { init(); return; }
        window.fbAsyncInit = init;
        if (!document.getElementById('facebook-jssdk')) {
            const s = document.createElement('script');
            s.id = 'facebook-jssdk';
            s.async = true;
            s.src = 'https://connect.facebook.net/pt_BR/sdk.js';
            document.head.appendChild(s);
        }
    }, [isPopupWindow, appId]);

    // --- FB SDK embedded signup flow ---
    const initiateLogin = useCallback(() => {
        if (hasCalledLogin.current) return;
        if (debugMode) return;

        if (!window.FB) {
            setErrorMessage('Facebook SDK ainda está carregando. Aguarde um instante e tente novamente.');
            return;
        }

        hasCalledLogin.current = true;
        updateStatus('processing');
        setProgressMessage('Aguardando autorização no Facebook...');

        const currentConfigId = configId || import.meta.env.VITE_FACEBOOK_CONFIG_ID || '1172468875039074';

        if (Config.dev) {
            const currentAppId = appId || import.meta.env.VITE_FACEBOOK_APP_ID || '1553356899550775';
            console.group('[FB SDK] Initiating login');
            console.log('App ID:', currentAppId);
            console.log('Config ID:', currentConfigId);
            console.log('Signup Mode:', signupMode);
            console.groupEnd();
        }

        window.FB.login(
            (response) => {
                if (response?.authResponse?.code) {
                    handleExchangeCode(response.authResponse.code);
                } else {
                    // Usuário fechou o modal ou cancelou
                    updateStatus('idle');
                    hasCalledLogin.current = false;
                    setSignupMode(null);
                }
            },
            { config_id: currentConfigId, response_type: 'code', override_default_response_type: true }
        );
    }, [appId, configId, signupMode, debugMode, updateStatus, handleExchangeCode]);

    // Setup listeners when login is initiated
    useEffect(() => {
        if (isPopupWindow) return;
        if (status !== 'processing') return;

        // Listener 1: BroadcastChannel (most reliable)
        let bc;
        try {
            bc = new BroadcastChannel(FB_CHANNEL_NAME);
            bc.onmessage = (event) => {
                if (event.data?.type === 'FB_CODE' && event.data?.code) {
                    handleExchangeCode(event.data.code);
                    try { if (popupRef.current) popupRef.current.close(); } catch (_) {}
                }
            };
        } catch (_) { /* BroadcastChannel not supported */ }

        // Listener 2: postMessage
        const handleMessage = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === 'FB_CODE' && event.data?.code) {
                handleExchangeCode(event.data.code);
                try { if (popupRef.current) popupRef.current.close(); } catch (_) {}
            }
        };

        // Listener 3: storage event
        const handleStorage = (event) => {
            if (event.key === FB_STORAGE_KEY && event.newValue) {
                localStorage.removeItem(FB_STORAGE_KEY);
                handleExchangeCode(event.newValue);
                try { if (popupRef.current) popupRef.current.close(); } catch (_) {}
            }
        };

        window.addEventListener('message', handleMessage);
        window.addEventListener('storage', handleStorage);

        return () => {
            window.removeEventListener('message', handleMessage);
            window.removeEventListener('storage', handleStorage);
            stopPolling();
            try { if (bc) bc.close(); } catch (_) {}
        };
    }, [isPopupWindow, status]);

    // Handle error params from OAuth redirect
    useEffect(() => {
        if (isPopupWindow) return;
        const error = params.get('error');
        const errorCode = params.get('error_code');
        const errorMsg = params.get('error_message');

        if (error || errorCode) {
            updateStatus('error');
            setErrorMessage(errorMsg || 'Erro ao conectar com o Facebook.');
        }
    }, []);

    const handleConnectClick = () => {
        hasCalledLogin.current = false;
        initiateLogin();
    };

    const saveTokenToChannel = async (token, phoneId, wabaId, businessId, expiresIn) => {
        updateStatus('updating_channel');
        setProgressMessage('Atualizando configurações do canal...');
        try {
            const {
                data: { data: canal },
            } = await v2_actionDetalhesCanal(channelId);
            if (!canal) {
                throw new Error('Canal não encontrado.');
            }

            const updatedConfig = { ...canal.config };
            if (!updatedConfig.meta) updatedConfig.meta = {};
            if (!updatedConfig.meta.whatsappBusinness) updatedConfig.meta.whatsappBusinness = {};
            updatedConfig.meta.whatsappBusinness.accessToken = token;
            if (phoneId) updatedConfig.meta.whatsappBusinness.fromNumberId = phoneId;
            if (wabaId) updatedConfig.meta.whatsappBusinness.wabaId = wabaId;
            if (businessId) updatedConfig.meta.whatsappBusinness.businessId = businessId;
            if (expiresIn) updatedConfig.meta.whatsappBusinness.tokenExpiresAt = new Date(Date.now() + expiresIn * 1000).toISOString();
            if (signupMode) updatedConfig.meta.whatsappBusinness.signupMode = signupMode;

            const payload = { action: 'editar_canal_front', scope: { ...canal, config: updatedConfig } };
            await v2_actionEditarCanal(channelId, payload);

            // Assina o webhook do WhatsApp (subscribed_apps) via backend.
            // Não-bloqueante: se falhar, o canal já está salvo e o job de lais-jobs
            // reprocessa depois — não devemos quebrar a conexão por causa disso.
            if (wabaId) {
                const baseUrl = Config.dev ? Config.url_dev : Config.urlBackend;

                try {
                    await axios.post(`${baseUrl}/api/v1/canais/subscribe-whatsapp-webhook`, { channelId });
                } catch (subErr) {
                    console.warn('[FB] Falha ao assinar webhook (subscribed_apps):', subErr?.response?.data || subErr.message);
                }

                // Atribui o system user da Lais à WABA (resolve a pendência de "vincular pessoa").
                try {
                    await axios.post(`${baseUrl}/api/v1/canais/assign-whatsapp-system-user`, { channelId });
                } catch (assignErr) {
                    console.warn('[FB] Falha ao atribuir system user (assigned_users):', assignErr?.response?.data || assignErr.message);
                }
            }

            updateStatus('success');
        } catch (error) {
            updateStatus('error');
            setErrorMessage('Erro ao salvar o token no canal. ' + (error.message || ''));
        }
    };

    const handleConfirmWaba = () => {
        if (!tokenData?.accessToken) return;
        saveTokenToChannel(
            tokenData.accessToken,
            selectedPhone?.id || '',
            selectedWaba?.wabaId || '',
            selectedWaba?.businessId || '',
            tokenData.expiresIn
        );
    };

    // --- Popup view: minimal UI just to close ---
    if (isPopup) {
        return (
            <div className="flex flex-col min-h-screen w-full items-center justify-center p-4 bg-slate-50">
                <Card className="w-full max-w-sm shadow-lg border border-gray-100 rounded-2xl bg-white">
                    <CardContent className="flex flex-col items-center justify-center p-8 gap-4 w-full">
                        <div className="h-14 w-14 bg-green-50 rounded-full flex items-center justify-center text-green-600">
                            <Iconify icon="mdi:check-circle" width={28} height={28} />
                        </div>
                        <div className="text-center space-y-1">
                            <h3 className="text-lg font-semibold text-gray-900">Login realizado!</h3>
                            <p className="text-sm text-gray-500">Você pode fechar esta janela.</p>
                        </div>
                        <button
                            onClick={() => { try { window.close(); } catch (_) {} }}
                            className="w-full mt-2 px-4 py-2.5 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95"
                        >
                            Fechar janela
                        </button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // --- Helper: quality rating display ---
    const qualityLabel = (rating) => {
        if (!rating || rating === 'UNKNOWN') return { text: 'Sem avaliação', color: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' };
        if (rating === 'GREEN') return { text: 'Alta', color: 'bg-green-50 text-green-700', dot: 'bg-green-500' };
        if (rating === 'YELLOW') return { text: 'Média', color: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' };
        return { text: 'Baixa', color: 'bg-red-50 text-red-700', dot: 'bg-red-500' };
    };

    // --- Parent view: main flow UI ---
    const isWabaStep = status === 'selecting_waba';
    const isChoiceStep = status === 'idle' && !signupMode;
    const isConnectStep = status === 'idle' && signupMode;

    return (
        <div className="flex flex-col min-h-screen w-full items-center justify-center p-4 bg-gradient-to-b from-slate-50 to-slate-100/50 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <Card className={`w-full shadow-xl border border-gray-100/80 rounded-2xl bg-white transition-all duration-500 ${isWabaStep ? 'max-w-lg' : 'max-w-md'}`}>
                <CardContent className="flex flex-col items-center justify-center p-8 gap-6 w-full">
                    {/* Header Icon */}
                    <div className="relative">
                        <div className="absolute inset-0 bg-[#1877F2]/10 blur-2xl rounded-full scale-150"></div>
                        <div className="relative bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                            <Iconify icon="logos:facebook" width={40} height={40} />
                        </div>
                    </div>

                    {/* Title */}
                    <div className="text-center space-y-1.5">
                        <h2 className="text-xl font-bold text-gray-900 tracking-tight">Conectar WhatsApp Business</h2>
                        <p className="text-sm text-gray-400">Integração oficial via Meta Business</p>
                    </div>

                    {/* Step 1: Mode Selection */}
                    {isChoiceStep && (
                        <div className="w-full flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <p className="text-sm text-gray-500 text-center">Como deseja conectar seu número?</p>
                            <div className="grid grid-cols-2 gap-3">
                                {/* Cloud API Card */}
                                <button
                                    onClick={() => setSignupMode('cloud_api')}
                                    className="p-4 rounded-xl border-2 border-gray-200 hover:border-[#7C3AED] hover:bg-violet-50/30 text-left transition-all duration-200 group"
                                >
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50 group-hover:bg-violet-100 transition-colors mb-3">
                                        <Iconify icon="ic:round-cloud" width={22} className="text-blue-600 group-hover:text-violet-600 transition-colors" />
                                    </div>
                                    <p className="text-sm font-bold text-gray-900 mb-1">Cloud API</p>
                                    <p className="text-xs text-gray-500 leading-relaxed">
                                        Migra o número completamente para a Cloud API da Meta
                                    </p>
                                    <div className="mt-3 flex items-center gap-1">
                                        <Iconify icon="eva:info-outline" width={12} className="text-gray-400" />
                                        <span className="text-[10px] text-gray-400">Recomendado para números novos</span>
                                    </div>
                                </button>

                                {/* Coexistence Card */}
                                <button
                                    onClick={() => setSignupMode('coexistence')}
                                    className="p-4 rounded-xl border-2 border-gray-200 hover:border-[#7C3AED] hover:bg-violet-50/30 text-left transition-all duration-200 group"
                                >
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-50 group-hover:bg-violet-100 transition-colors mb-3">
                                        <Iconify icon="eva:sync-outline" width={22} className="text-green-600 group-hover:text-violet-600 transition-colors" />
                                    </div>
                                    <p className="text-sm font-bold text-gray-900 mb-1">Coexistência</p>
                                    <p className="text-xs text-gray-500 leading-relaxed">
                                        Mantém o WhatsApp Business App e a Cloud API simultâneos
                                    </p>
                                    <div className="mt-3 flex items-center gap-1">
                                        <Iconify icon="eva:info-outline" width={12} className="text-gray-400" />
                                        <span className="text-[10px] text-gray-400">Números já em uso no WA Business</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Connect Button */}
                    {isConnectStep && (
                        <div className="w-full flex flex-col items-center gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            {/* Selected mode badge */}
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-100">
                                <Iconify
                                    icon={signupMode === 'cloud_api' ? 'ic:round-cloud' : 'eva:sync-outline'}
                                    width={14}
                                    className="text-violet-600"
                                />
                                <span className="text-xs font-medium text-violet-700">
                                    {signupMode === 'cloud_api' ? 'Cloud API' : 'Coexistência'}
                                </span>
                                <button
                                    onClick={() => setSignupMode(null)}
                                    className="ml-1 text-violet-400 hover:text-violet-600 transition-colors"
                                >
                                    <Iconify icon="eva:close-outline" width={14} />
                                </button>
                            </div>

                            <button
                                onClick={handleConnectClick}
                                className="w-full flex items-center justify-center gap-2.5 px-4 py-3 bg-[#1877F2] hover:bg-[#166FE5] text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-[#1877F2]/20 active:transform active:scale-[0.98]"
                            >
                                <Iconify icon="logos:facebook" width={20} />
                                Conectar com Facebook
                            </button>

                            <button
                                onClick={() => setSignupMode(null)}
                                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                Voltar
                            </button>
                        </div>
                    )}

                    {/* Status/Progress Section */}
                    {(status === 'processing' || status === 'exchanging_token' || status === 'updating_channel') && (
                        <div className="w-full flex flex-col items-center gap-5 p-6 rounded-xl bg-gradient-to-b from-gray-50 to-white border border-dashed border-gray-200">
                            <div className="relative">
                                <CircularProgress size={36} thickness={4} sx={{ color: '#7C3AED' }} />
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-semibold text-gray-700 mb-1">{progressMessage}</p>
                                <p className="text-xs text-gray-400">Por favor, não feche esta janela</p>
                            </div>

                            {/* Step progress bar */}
                            <div className="w-full max-w-[200px]">
                                <div className="flex items-center gap-1">
                                    <div className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                                        status === 'processing' || status === 'exchanging_token' || status === 'updating_channel' ? 'bg-[#7C3AED]' : 'bg-gray-200'
                                    }`} />
                                    <div className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                                        status === 'exchanging_token' || status === 'updating_channel' ? 'bg-[#7C3AED]' : 'bg-gray-200'
                                    }`} />
                                    <div className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                                        status === 'updating_channel' ? 'bg-[#7C3AED]' : 'bg-gray-200'
                                    }`} />
                                </div>
                                <div className="flex justify-between mt-1.5">
                                    <span className="text-[9px] text-gray-400">Login</span>
                                    <span className="text-[9px] text-gray-400">Token</span>
                                    <span className="text-[9px] text-gray-400">Salvar</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* WABA Selection Step */}
                    {isWabaStep && (
                        <div className="w-full flex flex-col gap-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            {/* Step indicator */}
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2 flex-1">
                                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                        <Iconify icon="eva:checkmark-fill" width={14} className="text-white" />
                                    </div>
                                    <div className="h-px flex-1 bg-green-300"></div>
                                    <div className="w-6 h-6 rounded-full bg-[#7C3AED] flex items-center justify-center">
                                        <span className="text-[10px] font-bold text-white">2</span>
                                    </div>
                                    <div className="h-px flex-1 bg-gray-200"></div>
                                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                                        <span className="text-[10px] font-bold text-gray-400">3</span>
                                    </div>
                                </div>
                            </div>

                            {/* Section title */}
                            <div>
                                <h3 className="text-base font-bold text-gray-900">Selecione a conta e o número</h3>
                                <p className="text-xs text-gray-400 mt-0.5">Escolha o WhatsApp Business Account e o número que deseja conectar</p>
                            </div>

                            {/* WABA selector */}
                            {wabas.length > 1 && (
                                <div>
                                    <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Conta Business</label>
                                    <div className="flex flex-col gap-2">
                                        {wabas.map((waba) => {
                                            const isSelected = selectedWaba?.wabaId === waba.wabaId;
                                            return (
                                                <button
                                                    key={waba.wabaId}
                                                    onClick={() => {
                                                        setSelectedWaba(waba);
                                                        setSelectedPhone(waba.phoneNumbers?.length === 1 ? waba.phoneNumbers[0] : null);
                                                    }}
                                                    className={`w-full p-3 rounded-xl border-2 text-left transition-all duration-200 ${
                                                        isSelected
                                                            ? 'border-[#7C3AED] bg-violet-50/50'
                                                            : 'border-gray-100 hover:border-gray-200'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                                                            isSelected ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 text-gray-400'
                                                        }`}>
                                                            <Iconify icon="ic:round-business" width={18} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-gray-900 truncate">{waba.wabaName || 'WhatsApp Business'}</p>
                                                            <p className="text-[10px] text-gray-400 font-mono truncate">{waba.businessName ? `${waba.businessName} · ` : ''}{waba.wabaId}</p>
                                                        </div>
                                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                                            isSelected ? 'border-[#7C3AED] bg-[#7C3AED]' : 'border-gray-300'
                                                        }`}>
                                                            {isSelected && <Iconify icon="eva:checkmark-fill" width={12} className="text-white" />}
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Phone number selector */}
                            {selectedWaba && selectedWaba.phoneNumbers?.length > 0 && (
                                <div className="animate-in fade-in duration-200">
                                    <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 block">
                                        Número de telefone
                                        {wabas.length <= 1 && selectedWaba && (
                                            <span className="normal-case tracking-normal font-normal text-gray-400 ml-1">
                                                — {selectedWaba.wabaName || 'WhatsApp Business'}
                                            </span>
                                        )}
                                    </label>
                                    <div className="flex flex-col gap-2">
                                        {selectedWaba.phoneNumbers.map((phone) => {
                                            const isPhoneSelected = selectedPhone?.id === phone.id;
                                            const quality = qualityLabel(phone.quality_rating);
                                            return (
                                                <button
                                                    key={phone.id}
                                                    onClick={() => setSelectedPhone(phone)}
                                                    className={`w-full p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${
                                                        isPhoneSelected
                                                            ? 'border-[#008069] bg-[#008069]/5'
                                                            : 'border-gray-100 hover:border-gray-200'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                                                            isPhoneSelected ? 'bg-[#008069] text-white' : 'bg-gray-50 text-gray-400'
                                                        }`}>
                                                            <Iconify icon="ic:round-whatsapp" width={20} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className={`text-sm font-bold truncate ${isPhoneSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                                                                {phone.display_phoneNumber || phone.id}
                                                            </p>
                                                            {phone.verified_name && (
                                                                <p className="text-xs text-gray-400 truncate mt-0.5">{phone.verified_name}</p>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${quality.color}`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${quality.dot}`} />
                                                                {quality.text}
                                                            </span>
                                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                                                isPhoneSelected ? 'border-[#008069] bg-[#008069]' : 'border-gray-300'
                                                            }`}>
                                                                {isPhoneSelected && <Iconify icon="eva:checkmark-fill" width={12} className="text-white" />}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Summary + Actions */}
                            <div className="pt-3 border-t border-gray-100">
                                {selectedWaba && selectedPhone && (
                                    <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 mb-3">
                                        <div className="w-8 h-8 rounded-lg bg-[#008069] flex items-center justify-center text-white flex-shrink-0">
                                            <Iconify icon="ic:round-whatsapp" width={16} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-gray-700 truncate">{selectedPhone.display_phoneNumber}</p>
                                            <p className="text-[10px] text-gray-400 truncate">{selectedWaba.wabaName}{selectedPhone.verified_name ? ` · ${selectedPhone.verified_name}` : ''}</p>
                                        </div>
                                        <Iconify icon="eva:checkmark-circle-2-fill" width={18} className="text-green-500 flex-shrink-0" />
                                    </div>
                                )}

                                <button
                                    onClick={handleConfirmWaba}
                                    disabled={!selectedWaba || !selectedPhone}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                                >
                                    <Iconify icon="eva:save-outline" width={18} />
                                    Confirmar e Salvar
                                </button>
                                <button
                                    onClick={() => {
                                        if (tokenData?.accessToken && channelId) {
                                            saveTokenToChannel(tokenData.accessToken, null, null, null, tokenData.expiresIn);
                                        }
                                    }}
                                    className="w-full mt-2 px-4 py-2 text-gray-400 hover:text-gray-600 text-xs font-medium transition-colors"
                                >
                                    Pular seleção (salvar apenas o token)
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Success State */}
                    {status === 'success' && (
                        <div className="w-full flex flex-col items-center gap-5 animate-in zoom-in duration-300">
                            <div className="relative">
                                <div className="absolute inset-0 bg-green-100 blur-xl rounded-full scale-150"></div>
                                <div className="relative h-16 w-16 bg-green-50 rounded-2xl flex items-center justify-center text-green-600 border border-green-100">
                                    <Iconify icon="eva:checkmark-circle-2-fill" width={32} />
                                </div>
                            </div>
                            <div className="text-center space-y-1.5">
                                <h3 className="text-lg font-bold text-gray-900">Conexão realizada!</h3>
                                <p className="text-sm text-gray-400 max-w-[260px] mx-auto">
                                    O token foi gerado e salvo no seu canal com sucesso.
                                </p>
                            </div>
                            {selectedWaba && (
                                <div className="w-full p-3 rounded-xl bg-gray-50 border border-gray-100">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-[#008069] flex items-center justify-center text-white flex-shrink-0">
                                            <Iconify icon="ic:round-whatsapp" width={18} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-semibold text-gray-700 truncate">{selectedWaba.wabaName}</p>
                                            {selectedPhone && <p className="text-[11px] text-gray-400 truncate">{selectedPhone.display_phoneNumber}</p>}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <button
                                onClick={() => navigate('/dashboard/configuracoes/canais')}
                                className="w-full mt-1 px-4 py-3 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-[0.98]"
                            >
                                Voltar para Canais
                            </button>
                        </div>
                    )}

                    {/* Error State */}
                    {status === 'error' && (
                        <div className="w-full flex flex-col items-center gap-4 animate-in zoom-in duration-300">
                            <div className="relative">
                                <div className="absolute inset-0 bg-red-100 blur-xl rounded-full scale-150"></div>
                                <div className="relative h-16 w-16 bg-red-50 rounded-2xl flex items-center justify-center text-red-500 border border-red-100">
                                    <Iconify icon="eva:alert-circle-fill" width={32} />
                                </div>
                            </div>
                            <div className="text-center space-y-1.5">
                                <h3 className="text-lg font-bold text-gray-900">Falha na conexão</h3>
                                <p className="text-sm text-red-400 max-w-[300px] mx-auto leading-relaxed">{errorMessage}</p>
                            </div>

                            {/* Debug details (dev only) */}
                            {Config.dev && errorDebugInfo && (
                                <div className="w-full">
                                    <button
                                        onClick={() => setShowDebugDetails(!showDebugDetails)}
                                        className="w-full text-left text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
                                    >
                                        <Iconify icon={showDebugDetails ? 'eva:chevron-down-outline' : 'eva:chevron-right-outline'} width={14} />
                                        Debug info
                                    </button>
                                    {showDebugDetails && (
                                        <pre className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 text-[10px] text-gray-600 overflow-auto max-h-[200px] whitespace-pre-wrap break-all">
                                            {JSON.stringify(errorDebugInfo, null, 2)}
                                        </pre>
                                    )}
                                </div>
                            )}

                            <div className="flex flex-col w-full gap-2 mt-1">
                                <button
                                    onClick={() => {
                                        setErrorMessage('');
                                        setErrorDebugInfo(null);
                                        hasCalledLogin.current = false;
                                        updateStatus('idle');
                                        setSignupMode(null);
                                    }}
                                    className="w-full px-4 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-xl transition-colors"
                                >
                                    Tentar Novamente
                                </button>
                                <button
                                    onClick={() => navigate('/dashboard/configuracoes/canais')}
                                    className="w-full px-4 py-2.5 text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Debug Panel (dev only) */}
            {Config.dev && !isPopupWindow && (
                <Card className="w-full max-w-md mt-4 shadow-sm border border-amber-200 rounded-2xl bg-amber-50/50">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Iconify icon="eva:code-outline" width={16} className="text-amber-600" />
                            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Debug Mode</span>
                            <label className="ml-auto flex items-center gap-1.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={debugMode}
                                    onChange={(e) => {
                                        setDebugMode(e.target.checked);
                                        if (e.target.checked) {
                                            stopPolling();
                                            updateStatus('idle');
                                            setSignupMode(null);
                                            setProgressMessage('Debug mode ativo - use o painel abaixo');
                                        }
                                    }}
                                    className="w-3.5 h-3.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                />
                                <span className="text-xs text-amber-700">Ativo</span>
                            </label>
                        </div>

                        {debugMode && (
                            <div className="flex flex-col gap-3 animate-in fade-in duration-200">
                                <div>
                                    <label className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-1 block">Cenário</label>
                                    <select
                                        value={debugScenario}
                                        onChange={(e) => setDebugScenario(e.target.value)}
                                        className="w-full text-xs border border-amber-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:border-amber-400 focus:ring-amber-400/20"
                                    >
                                        {DEBUG_SCENARIOS.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    onClick={handleDebugExchange}
                                    className="w-full px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
                                >
                                    Simular
                                </button>
                                <p className="text-[10px] text-amber-600/70">
                                    App ID: {import.meta.env.VITE_FACEBOOK_APP_ID || 'não definido'} | Config ID: {import.meta.env.VITE_FACEBOOK_CONFIG_ID || 'não definido'}
                                    <br />Mode: {signupMode || 'não selecionado'}
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
